import React from 'react';
import { ApiAttributeMapping, ApiAttributeMappingFieldEnum, ApiNote, ApiPerson, ApiPersonCategory } from '../api';
import { formatDate } from './datetime/FormattedDateTime';
import {
  InternalApiMonitoringAlert,
  InternalApiNote,
  InternalApiPerson,
  InternalApiPersonCategory,
  InternalApiPersonType,
  InternalApiSnoozeConfig,
} from '../api-internal';
import { DropdownOption } from '@salvapp/ui-components/build/dropdown/dropdown.types';
import './helper.scss';

export const FormattedAlertReasons = ({ alert }: { alert: InternalApiMonitoringAlert }) => {
  const results = alert.score;
  let alertReasons: string;
  if (results.length > 1) {
    alertReasons = `${results[0].reason} • ${results.length - 1} more`;
  } else {
    alertReasons = results[0].reason;
  }
  return <span className={'content-preview-prefix'}>{alertReasons}</span>;
};

export const FormattedAlertNote = ({ apiNote }: { apiNote: ApiNote | InternalApiNote }) => {
  const userName = apiNote.user?.name ?? 'System';
  return <span className={'content-preview-prefix'}>{`${userName}, ${formatDate(apiNote.createdTime!)} •`}</span>;
};

export const getAttributeMappingFieldName = (
  field: ApiAttributeMappingFieldEnum,
  mappings: { [key: string]: ApiAttributeMapping }
): string => {
  const attribute = mappings[field.toString()];
  return attribute ? attribute.mapping : '';
};

export const getPersonDisplayName = (
  person: InternalApiPerson | ApiPerson,
  mappings: { [key: string]: ApiAttributeMapping }
) => {
  const { type, attributes, id, category } = person;
  const name = attributes?.[getAttributeMappingFieldName(ApiAttributeMappingFieldEnum.COMPANY_NAME, mappings)];
  if (name && name !== '' && (type === InternalApiPersonType.BUSINESS || type === InternalApiPersonType.LEGAL)) {
    return name;
  } else if (
    type === InternalApiPersonType.UNDEFINED &&
    category == InternalApiPersonCategory.COUNTERPARTY &&
    attributes?.['counterpartyName'] !== undefined
  ) {
    return attributes?.['counterpartyName'];
  }
  const firstName = attributes?.[getAttributeMappingFieldName(ApiAttributeMappingFieldEnum.FIRST_NAME, mappings)];
  const lastName = attributes?.[getAttributeMappingFieldName(ApiAttributeMappingFieldEnum.LAST_NAME, mappings)];
  if (firstName || lastName) {
    return `${firstName || ''} ${lastName || ''}`;
  }
  return id;
};

export const FormattedSnoozeConfiguration = ({ snoozeConfig }: { snoozeConfig: InternalApiSnoozeConfig }) => {
  const userName = snoozeConfig.createdBy.name;
  const until = snoozeConfig.endTime ? ` until ${formatDate(snoozeConfig.endTime)}` : '';
  const total = snoozeConfig.totalAmount ? ` for total amount of ${snoozeConfig.totalAmount}` : '';
  return <span className={'content-preview-prefix'}>{`${userName} snoozed alerts${until}${total} • `}</span>;
};

export const formatEnumLabel = (status?: string): string => {
  if (!status) {
    return '';
  }
  return `${status[0].toUpperCase()}${status.substring(1).toLowerCase().replace(/_/g, ' ')}`;
};

export const parseJsonSilent = (input: string): any => {
  try {
    return JSON.parse(input);
  } catch (_) {
    return {};
  }
};

export const isNavigableCellClicked = (event: React.MouseEvent): boolean => {
  const target = event.target as HTMLElement;
  return (
    target.tagName === 'TD' && target.parentElement !== null && target.parentElement.classList.contains('clickable-row')
  );
};

export const numberFromQuery = (searchParams: URLSearchParams, name: string, defaultValue: number): number => {
  const stringValue = searchParams.get(name);
  if (stringValue === null || stringValue.length === 0) {
    return defaultValue;
  }
  const numValue = Number.parseInt(stringValue, 10);
  return Number.isNaN(numValue) ? defaultValue : numValue;
};

export const isCounterparty = (person?: ApiPerson | InternalApiPerson): boolean =>
  person?.category === ApiPersonCategory.COUNTERPARTY;

export const lengthOrZero = (arr: unknown[]): number => arr?.length || 0;
export const pluralize = (count: number, singular: string, plural: string, withValue = false): string => {
  let word: string;
  if (count === 1) {
    word = singular;
  } else {
    word = plural;
  }

  if (withValue) {
    return `${count} ${word}`;
  } else {
    return word;
  }
};

export const assertExhaustive = (_: never): never => {
  throw new Error('Invalid input');
};

export const trimValue = (text: string, length: number, addEllipsis = true): string => {
  const trimmed = text.slice(0, length);
  if (text.length > trimmed.length && addEllipsis) {
    return `${trimmed}${addEllipsis && '…'}`;
  } else {
    return trimmed;
  }
};

export const snakeToCamelCase = (snakeCaseWord: string) =>
  snakeCaseWord.replace(/(_[a-zA-Z])/g, (group) => group.toUpperCase().replace('_', ''));

export const toSnakeCase = (str: string) => {
  return str
    .replace(/\s+/g, '_')
    .replace(/[^a-zA-Z0-9_]/g, '')
    .toLowerCase();
};

export const fromSnakeCase = (str: string) => {
  return str
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/^./, (match) => match.toUpperCase());
};

export const camelCaseToSentence = (str: string) => {
  return str.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/^./, (match) => match.toUpperCase());
};

export const convertToDropdownOption = (name: string, id?: number | string, disabled?: boolean): DropdownOption => {
  return { label: name, key: String(id), value: String(id), disabled: disabled };
};
export default {
  FormattedAlertReasons,
  FormattedAlertNote,
  getAttributeMappingValue: getAttributeMappingFieldName,
  getPersonDisplayName,
  FormattedSnoozeConfiguration,
  formatEnumLabel,
  parseJsonSilent,
  isNavigableCellClicked,
  numberFromQuery,
  trimValue,
};

export const dateTimeFormatRegex = new RegExp('^\\d{4}-\\d{2}-\\d{2} \\d{2}:\\d{2}$');
export const DATE_FORMAT = 'YYYY-MM-DD';
export const DATE_TIME_FORMAT = 'YYYY-MM-DD HH:mm';
export const ZONED_DATE_TIME_FORMAT = 'YYYY-MM-DDTHH:mm:ssZ';
export const INPUT_REGEX = new RegExp(/^.*<(“[^”]*”|'[^’]*’|[^'”>])*>.*$/);

export const FULL_DATE_TIME_FORMAT = 'MMM DD, YYYY HH:mm';
export const FULL_DATE_FORMAT = 'MMM DD, YYYY';

export const testInput = (value: string | undefined): boolean => {
  if (value) {
    return !INPUT_REGEX.test(value);
  }
  return true;
};

export const isDefined = <T,>(x: T | undefined): x is T => {
  return !!x;
};

export const highlightInput = (input: HTMLTextAreaElement | HTMLInputElement) => {
  input.focus();
  input.setSelectionRange(0, input.value.length);
};
